import React from "react";
import FloatingActionBar from "../../../library/FloatingActionBar";
import SelectDropdown from "../../../library/SelectDropdown";
import { StyledDestructiveBulkButton } from "../../../library/BulkToolbarButtons";
import { getProgressLabel } from "../../../../utils/LinkedProgrammeUtils";
import PermissionsGate from "../../../../utils/permissionGate";
import {
    StyledBulkButton,
    StyledBulkVersion,
    StyledDisabledBulkAction,
    StyledProcessingButton,
} from "./LinkedProgrammeBulkActions.styles";

/**
 * Small pill shown in place of a bulk action while it's mid-flight.
 * @param {"lock"|"unlock"|"setVersion"} type Which bulk action is processing.
 */
function ProcessingButton({ type }) {
    return (
        <StyledProcessingButton
            type="button"
            aria-label={getProgressLabel(type)}
        >
      <i
                className="fal fa-sync fa-spin"
                aria-hidden="true"
            />
      {getProgressLabel(type)}...
    </StyledProcessingButton>
    );
}

/**
 * Floating bulk-action bar for the linked programmes table — set version,
 * lock/unlock grades, and unlink for the currently selected rows.
 * @param {boolean} isVersionEnabled Whether the "Set Version" action is shown.
 * @param {{type: string}} progress The bulk action currently in flight, if any.
 * @param {Array} versionOptions Options for the "Set Version" dropdown.
 * @param {Function} setSetVersionValue Stores the version chosen before confirming.
 * @param {Function} startConfirmation Opens the confirmation modal for an action.
 * @param {React.Ref} [barRef] Ref forwarded to the underlying bar element, so callers can measure its rendered height.
 */
function LinkedProgrammeBulkActions({
    selectedIds,
    isVersionEnabled,
    progress,
    versionOptions,
    setSetVersionValue,
    startConfirmation,
    barRef,
}) {
    if (!selectedIds.length) {
        return null;
    }

    const disabled = Boolean(progress);

    return (
        <FloatingActionBar
            ref={barRef}
            summary={`${selectedIds.length} Programme${
                selectedIds.length === 1 ? "" : "s"
            } selected`}
        >
      {isVersionEnabled &&
                versionOptions?.length > 0 &&
                (progress?.type === "setVersion" ? (
                    <ProcessingButton type="setVersion" />
                ) : (
                    <StyledDisabledBulkAction
                        $disabled={disabled}
                    >
            <StyledBulkVersion>
              <PermissionsGate scopes={["matpedit"]} errorProps={{ disabled: true }}>
                <SelectDropdown
                                    variant="bulk"
                                    size="sm"
                                    placeholder="Set Version"
                                    value=""
                                    options={versionOptions}
                                    disabled={disabled}
                                    onChange={(event) => {
                                        const nextValue = event?.target?.value ?? "";

                                        if (!nextValue) {
                                            return;
                                        }

                                        setSetVersionValue(nextValue);
                                        startConfirmation(
                                            "setVersion",
                                            selectedIds
                                        );
                                    }}
                                    ariaLabel="Set Version"
                                />
              </PermissionsGate>
            </StyledBulkVersion>
          </StyledDisabledBulkAction>
                ))}

      {progress?.type === "lock" ? (
                <ProcessingButton type="lock" />
            ) : (
                <StyledDisabledBulkAction
                    $disabled={disabled}
                >
          <PermissionsGate scopes={["capgsedit"]} errorProps={{ disabled: true }}>
            <StyledBulkButton
                            type="button"
                            $iconTone="lock"
                            disabled={disabled}
                            onClick={() =>
                                startConfirmation(
                                    "lock",
                                    selectedIds
                                )
                            }
                        >
              <i
                                className="fas fa-lock"
                                aria-hidden="true"
                            />
              Lock All Grades
            </StyledBulkButton>
          </PermissionsGate>
        </StyledDisabledBulkAction>
            )}

      {progress?.type === "unlock" ? (
                <ProcessingButton type="unlock" />
            ) : (
                <StyledDisabledBulkAction
                    $disabled={disabled}
                >
          <PermissionsGate scopes={["capgsedit"]} errorProps={{ disabled: true }}>
            <StyledBulkButton
                            type="button"
                            $iconTone="unlock"
                            disabled={disabled}
                            onClick={() =>
                                startConfirmation(
                                    "unlock",
                                    selectedIds
                                )
                            }
                        >
              <i
                                className="fas fa-unlock"
                                aria-hidden="true"
                            />
              Unlock All Grades
            </StyledBulkButton>
          </PermissionsGate>
        </StyledDisabledBulkAction>
            )}

      <StyledDisabledBulkAction
                $disabled={disabled}
            >
        <PermissionsGate scopes={["matpadd"]} errorProps={{ disabled: true }}>
          <StyledDestructiveBulkButton
                        type="button"
                        disabled={disabled}
                        onClick={() =>
                            startConfirmation(
                                "unlink",
                                selectedIds
                            )
                        }
                    >
            <i
                            className="fal fa-unlink"
                            aria-hidden="true"
                        />
            Unlink Programme
          </StyledDestructiveBulkButton>
        </PermissionsGate>
      </StyledDisabledBulkAction>
    </FloatingActionBar>
    );
}

export default LinkedProgrammeBulkActions;
